// AddonsManager.jsx - Tab in BookingSettings to manage add-ons per service
// Each service can have its own list of add-ons (Vitamin C, LED therapy, etc.)

import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { useIsMobile } from "./useIsMobile";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {width:"100%",padding:"9px 12px",background:C.light,border:"1px solid "+C.border,borderRadius:7,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box"};

export default function AddonsManager() {
  const isMobile = useIsMobile();
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [addons, setAddons] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null); // null | 'new' | addon object
  const [copying, setCopying] = useState(null); // addon being cloned to other services
  const [flash, setFlash] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadServices(); }, []);
  useEffect(() => { if (selectedService) loadAddons(selectedService.id); else setAddons([]); }, [selectedService]);

  const loadServices = async () => {
    setLoading(true);
    const { data } = await supabase.from("services").select("id, name, category, price").order("category").order("name");
    setServices(data || []);
    setLoading(false);
  };

  const loadAddons = async (serviceId) => {
    const { data } = await supabase.from("service_addons").select("*").eq("service_id", serviceId).order("display_order").order("id");
    setAddons(data || []);
  };

  const filteredServices = services.filter(s => 
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.category||"").toLowerCase().includes(search.toLowerCase())
  );

  const addonCounts = {};
  // Will be populated when user clicks each service - but show all in count badge
  const [allCounts, setAllCounts] = useState({});
  useEffect(() => {
    const loadAllCounts = async () => {
      const { data } = await supabase.from("service_addons").select("service_id");
      const counts = {};
      (data || []).forEach(a => { counts[a.service_id] = (counts[a.service_id]||0) + 1; });
      setAllCounts(counts);
    };
    loadAllCounts();
  }, [addons]);

  return (
    <div>
      <div style={{marginBottom:18}}>
        <div style={{fontSize:14,fontWeight:700,color:C.gold,fontFamily:S}}>Add-ons & Upsells</div>
        <div style={{fontSize:11,color:C.muted}}>Add upsell offers shown after a client picks each service. Boosts average order value.</div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:isMobile?"1fr":"320px 1fr",gap:14}}>
        {/* LEFT: Services list — hide on mobile when service selected, show full-width otherwise */}
        {(!isMobile || !selectedService) && (
        <div style={{background:C.card,border:"1px solid "+C.border,borderRadius:10,overflow:"hidden",height:"fit-content",maxHeight:isMobile?420:600,display:"flex",flexDirection:"column"}}>
          <div style={{padding:"10px 12px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Search services..." style={{...inp,fontSize:12,padding:"7px 10px"}}/>
          </div>
          <div style={{overflow:"auto",flex:1}}>
            {loading && <div style={{padding:20,textAlign:"center",color:C.muted,fontSize:12}}>Loading...</div>}
            {filteredServices.map(s => {
              const count = allCounts[s.id] || 0;
              const isSelected = selectedService?.id === s.id;
              return (
                <button key={s.id} onClick={()=>setSelectedService(s)} style={{
                  width:"100%",
                  padding:"10px 12px",
                  background: isSelected ? C.goldBg : "transparent",
                  border: "none",
                  borderBottom: "1px solid "+C.border,
                  borderLeft: "3px solid " + (isSelected ? C.gold : "transparent"),
                  cursor:"pointer",
                  textAlign:"left",
                  fontFamily:F,
                  display:"flex",
                  justifyContent:"space-between",
                  alignItems:"center",
                  gap:8,
                }}>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:isSelected?700:500,color:C.text,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{s.name}</div>
                    <div style={{fontSize:10,color:C.dim}}>{s.category}</div>
                  </div>
                  {count > 0 && (
                    <span style={{padding:"2px 7px",background:C.gold,color:"#fff",borderRadius:10,fontSize:10,fontWeight:700,flexShrink:0}}>{count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        )}

        {/* RIGHT: Add-ons editor — hidden on mobile until a service is picked, then takes full width */}
        {(!isMobile || selectedService) && (
        <div>
          {!selectedService ? (
            <div style={{padding:60,textAlign:"center",background:C.card,border:"1px solid "+C.border,borderRadius:10,color:C.muted}}>
              <div style={{fontSize:36,marginBottom:8}}>👈</div>
              <div style={{fontSize:14,fontWeight:600,color:C.text,marginBottom:4}}>Pick a service from the left</div>
              <div style={{fontSize:12}}>To add or manage upsell offers</div>
            </div>
          ) : (
            <div>
              {isMobile && (
                <button onClick={()=>setSelectedService(null)} style={{
                  marginBottom:10, padding:"8px 12px",
                  background:C.surface, border:"1px solid "+C.border,
                  borderRadius:8, fontSize:12, fontWeight:700, color:C.gold,
                  cursor:"pointer", fontFamily:F, minHeight:40,
                }}>← Back to services</button>
              )}
              <div style={{padding:14,background:C.goldBg,border:"1px solid "+C.borderGold,borderRadius:10,marginBottom:12}}>
                <div style={{fontSize:11,color:C.muted,fontWeight:600,textTransform:"uppercase",letterSpacing:"0.06em"}}>Add-ons for</div>
                <div style={{fontSize:18,fontWeight:800,color:C.text,fontFamily:S,marginTop:2}}>{selectedService.name}</div>
                <div style={{fontSize:11,color:C.muted}}>{selectedService.category} · ${selectedService.price}</div>
              </div>

              <button onClick={()=>setEditing("new")} style={{
                marginBottom:12,
                padding:"10px 16px",
                background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",
                color:"#fff",
                border:"none",
                borderRadius:8,
                fontSize:12,
                fontWeight:700,
                cursor:"pointer",
                fontFamily:F,
              }}>
                + Add New Upsell
              </button>

              {addons.length === 0 ? (
                <div style={{padding:30,textAlign:"center",background:C.light,border:"1px dashed "+C.border,borderRadius:10,color:C.muted,fontSize:12}}>
                  No add-ons yet for this service. Add one to start upselling!
                </div>
              ) : (
                <div style={{display:"flex",flexDirection:"column",gap:8}}>
                  {addons.map(a => (
                    <AddonCard key={a.id} addon={a}
                      onEdit={()=>setEditing(a)}
                      onCopy={()=>setCopying(a)}
                      onDelete={async()=>{
                        if (!window.confirm("Delete '"+a.name+"'?")) return;
                        await supabase.from("service_addons").delete().eq("id", a.id);
                        loadAddons(selectedService.id);
                      }}/>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        )}
      </div>

      {/* Editor Modal */}
      {editing && selectedService && (
        <AddonEditor
          serviceId={selectedService.id}
          serviceName={selectedService.name}
          addon={editing === "new" ? null : editing}
          onClose={()=>setEditing(null)}
          onSaved={()=>{
            loadAddons(selectedService.id);
            setEditing(null);
          }}
        />
      )}

      {/* Copy-to-services Modal */}
      {copying && (
        <CopyAddonModal
          addon={copying}
          sourceServiceId={selectedService?.id}
          services={services}
          onClose={()=>setCopying(null)}
          onDone={(count) => {
            setCopying(null);
            setFlash(`✓ Copied "${copying.name}" to ${count} service${count !== 1 ? "s" : ""}`);
            // Refresh add-on counts and current-view list
            (async () => {
              const { data } = await supabase.from("service_addons").select("service_id");
              const counts = {};
              (data || []).forEach(a => { counts[a.service_id] = (counts[a.service_id]||0) + 1; });
              setAllCounts(counts);
              if (selectedService) loadAddons(selectedService.id);
            })();
            setTimeout(() => setFlash(""), 3500);
          }}
        />
      )}

      {/* Flash banner */}
      {flash && (
        <div style={{position:"fixed",bottom:20,right:20,padding:"12px 18px",background:"linear-gradient(135deg,"+C.green+",#1e6b3f)",color:"#fff",borderRadius:10,fontSize:13,fontWeight:700,boxShadow:"0 10px 30px rgba(45,138,85,0.35)",zIndex:3000,fontFamily:F}}>{flash}</div>
      )}
    </div>
  );
}

function AddonCard({ addon, onEdit, onDelete, onCopy }) {
  const hasDeal = addon.sale_price && addon.sale_price < addon.regular_price;
  const savings = hasDeal ? Math.round((1 - addon.sale_price / addon.regular_price) * 100) : 0;

  return (
    <div style={{padding:14,background:C.card,border:"1px solid "+C.border,borderRadius:10,display:"flex",alignItems:"center",gap:14}}>
      {addon.image_url && (
        <img src={addon.image_url} alt={addon.name} style={{width:60,height:60,objectFit:"cover",borderRadius:8,flexShrink:0}}/>
      )}
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:3}}>
          <div style={{fontSize:14,fontWeight:700,color:C.text}}>{addon.name}</div>
          {!addon.is_active && <span style={{fontSize:10,padding:"2px 7px",background:C.redL,color:C.red,borderRadius:10,fontWeight:600}}>INACTIVE</span>}
        </div>
        {addon.description && <div style={{fontSize:11,color:C.muted,marginBottom:5}}>{addon.description}</div>}
        <div style={{display:"flex",alignItems:"center",gap:8,fontSize:11}}>
          {hasDeal ? (
            <>
              <span style={{textDecoration:"line-through",color:C.muted}}>${addon.regular_price}</span>
              <span style={{fontWeight:800,color:C.green,fontSize:14}}>${addon.sale_price}</span>
              <span style={{fontSize:10,padding:"2px 6px",background:C.greenL,color:C.green,borderRadius:10,fontWeight:700}}>SAVE {savings}%</span>
            </>
          ) : (
            <span style={{fontWeight:800,color:C.gold,fontSize:14}}>${addon.regular_price}</span>
          )}
          {addon.duration_add_minutes > 0 && (
            <span style={{color:C.muted,marginLeft:6}}>· +{addon.duration_add_minutes} min</span>
          )}
        </div>
      </div>
      <div style={{display:"flex",gap:6,flexWrap:"wrap",justifyContent:"flex-end"}}>
        <button onClick={onCopy} title="Apply this add-on to other services" style={{padding:"7px 12px",background:"#fff",color:C.purple,border:"1px solid "+C.purple+"40",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>📋 Copy to…</button>
        <button onClick={onEdit} style={{padding:"7px 12px",background:C.goldBg,color:C.gold,border:"1px solid "+C.borderGold,borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F}}>Edit</button>
        <button onClick={onDelete} style={{padding:"7px 12px",background:C.redL,color:C.red,border:"1px solid "+C.red+"30",borderRadius:6,fontSize:11,cursor:"pointer",fontFamily:F}}>🗑</button>
      </div>
    </div>
  );
}

// Modal — pick multiple target services to clone this add-on into.
// Skips services that already have an add-on with the same name.
function CopyAddonModal({ addon, sourceServiceId, services, onClose, onDone }) {
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [hasAddonByService, setHasAddonByService] = useState({}); // service_id → true if already has this name

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from("service_addons").select("service_id,name").ilike("name", addon.name);
      const map = {};
      (data || []).forEach(r => {
        if ((r.name || "").trim().toLowerCase() === (addon.name || "").trim().toLowerCase()) {
          map[r.service_id] = true;
        }
      });
      setHasAddonByService(map);
    })();
  }, [addon.name]);

  const targets = services.filter(s => s.id !== sourceServiceId && !hasAddonByService[s.id]);
  const filtered = targets.filter(s =>
    !search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.category||"").toLowerCase().includes(search.toLowerCase())
  );
  const byCategory = {};
  filtered.forEach(s => {
    const cat = s.category || "Uncategorized";
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(s);
  });

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const toggleCategory = (svcs) => {
    const ids = svcs.map(s => s.id);
    const allOn = ids.every(id => selected.has(id));
    const next = new Set(selected);
    if (allOn) ids.forEach(id => next.delete(id));
    else ids.forEach(id => next.add(id));
    setSelected(next);
  };

  const doCopy = async () => {
    if (selected.size === 0) return;
    setBusy(true);
    const baseId = Date.now();
    const rows = [...selected].map((sid, i) => ({
      id: baseId + i,
      service_id: sid,
      name: addon.name,
      description: addon.description || null,
      regular_price: addon.regular_price,
      sale_price: addon.sale_price || null,
      duration_add_minutes: addon.duration_add_minutes || 0,
      image_url: addon.image_url || null,
      display_order: addon.display_order || 0,
      is_active: addon.is_active !== false,
    }));
    const { error } = await supabase.from("service_addons").insert(rows);
    setBusy(false);
    if (error) { alert("Copy failed: " + error.message); return; }
    onDone(selected.size);
  };

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}>
      <div style={{background:"#fff",borderRadius:14,width:"100%",maxWidth:600,padding:24,maxHeight:"90vh",overflowY:"auto",position:"relative"}}>
        <button onClick={onClose} style={{position:"absolute",top:12,right:12,background:C.light,border:"none",borderRadius:7,padding:"4px 10px",cursor:"pointer",fontSize:16,color:C.muted}}>×</button>
        <div style={{fontSize:18,fontWeight:800,color:C.gold,fontFamily:S,marginBottom:6}}>📋 Copy "{addon.name}" to other services</div>
        <div style={{fontSize:12,color:C.muted,marginBottom:14}}>Pick the services where this add-on should also appear. ${addon.regular_price}{addon.sale_price && addon.sale_price < addon.regular_price ? ` / sale $${addon.sale_price}`:""}{addon.duration_add_minutes ? ` · +${addon.duration_add_minutes} min` : ""}</div>

        <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Filter services…" style={{...inp, marginBottom:12}}/>

        {Object.keys(byCategory).length === 0 && (
          <div style={{padding:30,textAlign:"center",color:C.muted,fontSize:12,background:C.light,borderRadius:10,border:"1px dashed "+C.border,marginBottom:14}}>
            {targets.length === 0 ? "All other services already have this add-on." : "No services match your filter."}
          </div>
        )}

        <div style={{maxHeight:340,overflowY:"auto",border:"1px solid "+C.border,borderRadius:10,padding:8,marginBottom:14}}>
          {Object.entries(byCategory).map(([cat, svcs]) => {
            const allOn = svcs.every(s => selected.has(s.id));
            const someOn = svcs.some(s => selected.has(s.id));
            return (
              <div key={cat} style={{marginBottom:8}}>
                <label style={{display:"flex",alignItems:"center",gap:8,padding:"5px 7px",cursor:"pointer",borderRadius:6,background:C.goldBg,marginBottom:4}}>
                  <input type="checkbox" checked={allOn} ref={el=>{if(el) el.indeterminate = someOn && !allOn;}} onChange={()=>toggleCategory(svcs)} style={{accentColor:C.gold}}/>
                  <span style={{fontSize:11,fontWeight:800,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em"}}>{cat}</span>
                  <span style={{fontSize:10,color:C.muted,marginLeft:"auto"}}>{svcs.filter(s=>selected.has(s.id)).length}/{svcs.length}</span>
                </label>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:4,paddingLeft:8}}>
                  {svcs.map(s => (
                    <label key={s.id} style={{display:"flex",alignItems:"center",gap:6,padding:"5px 7px",cursor:"pointer",borderRadius:5,background:selected.has(s.id)?C.goldBg:"transparent"}}>
                      <input type="checkbox" checked={selected.has(s.id)} onChange={()=>toggle(s.id)} style={{accentColor:C.gold}}/>
                      <span style={{fontSize:12,color:C.text}}>{s.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{display:"flex",gap:8,justifyContent:"space-between",alignItems:"center",flexWrap:"wrap"}}>
          <div style={{fontSize:12,color:C.muted}}>
            {Object.keys(hasAddonByService).length > 0 && (
              <span>ℹ️ {Object.keys(hasAddonByService).length} service{Object.keys(hasAddonByService).length>1?"s":""} already have this add-on — hidden</span>
            )}
          </div>
          <div style={{display:"flex",gap:8}}>
            <button onClick={onClose} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:8,fontSize:12,cursor:"pointer",fontFamily:F}}>Cancel</button>
            <button onClick={doCopy} disabled={selected.size === 0 || busy} style={{padding:"9px 20px",background:(selected.size===0||busy)?C.light:"linear-gradient(135deg,"+C.purple+",#5b21b6)",color:(selected.size===0||busy)?C.muted:"#fff",border:"none",borderRadius:8,fontSize:12,fontWeight:700,cursor:(selected.size===0||busy)?"not-allowed":"pointer",fontFamily:F}}>
              {busy ? "Copying…" : `📋 Copy to ${selected.size || "—"} service${selected.size === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// Image uploader for add-ons - uploads to Supabase Storage
function AddonImageUploader({ currentUrl, onUploaded }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setError("File too large (max 5MB)");
      return;
    }
    setUploading(true);
    setError("");
    try {
      const ext = file.name.split(".").pop().toLowerCase();
      const fileName = `addon-${Date.now()}-${Math.random().toString(36).slice(2,8)}.${ext}`;
      const { error: upErr } = await supabase.storage
        .from("email-images")
        .upload(fileName, file, { contentType: file.type, cacheControl: "31536000" });
      if (upErr) throw upErr;
      const { data } = supabase.storage.from("email-images").getPublicUrl(fileName);
      onUploaded(data.publicUrl);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {currentUrl && (
        <div style={{ position:"relative",display:"inline-block",marginBottom:8 }}>
          <img src={currentUrl} alt="addon" style={{ maxWidth:"100%",maxHeight:140,borderRadius:8,display:"block",border:"1px solid "+C.border }} />
          <button onClick={() => onUploaded("")} style={{ position:"absolute",top:6,right:6,width:24,height:24,background:"rgba(0,0,0,0.7)",color:"#fff",border:"none",borderRadius:"50%",cursor:"pointer",fontSize:12 }}>×</button>
        </div>
      )}
      <label style={{ display:"flex",alignItems:"center",justifyContent:"center",gap:8,padding:"10px 14px",background:C.goldBg,color:C.gold,border:"1px dashed "+C.borderGold,borderRadius:8,cursor:uploading?"wait":"pointer",fontSize:12,fontWeight:600,fontFamily:F }}>
        <input type="file" accept="image/*" disabled={uploading} onChange={e => upload(e.target.files?.[0])} style={{ display:"none" }} />
        {uploading ? "⏳ Uploading..." : currentUrl ? "🔄 Replace image" : "📁 Choose image (JPG/PNG, max 5MB)"}
      </label>
      {error && <div style={{ marginTop:6,fontSize:11,color:C.red }}>❌ {error}</div>}
    </div>
  );
}

function AddonEditor({ serviceId, serviceName, addon, onClose, onSaved }) {
  const [form, setForm] = useState(addon || {
    service_id: serviceId,
    name: "",
    description: "",
    regular_price: 0,
    sale_price: null,
    duration_add_minutes: 0,
    image_url: "",
    is_active: true,
    display_order: 0,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const update = (k, v) => setForm(f => ({...f, [k]: v}));

  const save = async () => {
    if (!form.name?.trim()) { setError("Name is required"); return; }
    if (!form.regular_price || form.regular_price <= 0) { setError("Regular price required"); return; }

    setSaving(true);
    setError("");

    const payload = {
      service_id: serviceId,
      name: form.name.trim(),
      description: form.description?.trim() || null,
      regular_price: +form.regular_price,
      sale_price: form.sale_price ? +form.sale_price : null,
      duration_add_minutes: +form.duration_add_minutes || 0,
      image_url: form.image_url?.trim() || null,
      is_active: form.is_active !== false,
      display_order: +form.display_order || 0,
    };

    let err;
    if (addon?.id) {
      const { error } = await supabase.from("service_addons").update(payload).eq("id", addon.id);
      err = error;
    } else {
      const { error } = await supabase.from("service_addons").insert([payload]);
      err = error;
    }
    setSaving(false);
    if (err) setError(err.message);
    else onSaved();
  };

  return (
    <div onClick={onClose} style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.6)",zIndex:2000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)"}}>
      <div onClick={e=>e.stopPropagation()} style={{background:C.surface,borderRadius:14,maxWidth:520,width:"100%",maxHeight:"90vh",overflow:"auto"}}>
        <div style={{padding:"18px 22px",borderBottom:"1px solid "+C.border,background:C.goldBg}}>
          <div style={{fontSize:16,fontWeight:800,color:C.text,fontFamily:S}}>{addon ? "Edit Add-on" : "New Add-on"}</div>
          <div style={{fontSize:11,color:C.muted}}>For: {serviceName}</div>
        </div>

        <div style={{padding:22}}>
          <Field label="Name *" hint="Short, benefit-focused name (e.g. 'Vitamin C Boost')">
            <input value={form.name} onChange={e=>update("name", e.target.value)} style={inp} placeholder="Vitamin C Boost"/>
          </Field>

          <Field label="Description" hint="Why should they add it? (1-2 sentences)">
            <textarea value={form.description||""} onChange={e=>update("description", e.target.value)} rows={2} style={{...inp, resize:"vertical"}} placeholder="Brightens skin, evens tone, and boosts the glow factor"/>
          </Field>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="Regular Price ($) *">
              <input type="number" step="0.01" value={form.regular_price||""} onChange={e=>update("regular_price", e.target.value)} style={inp} placeholder="99"/>
            </Field>
            <Field label="Sale Price ($)" hint="Leave empty if no sale">
              <input type="number" step="0.01" value={form.sale_price||""} onChange={e=>update("sale_price", e.target.value)} style={inp} placeholder="59"/>
            </Field>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10}}>
            <Field label="Adds Duration (min)" hint="0 if no extra time">
              <input type="number" value={form.duration_add_minutes||0} onChange={e=>update("duration_add_minutes", e.target.value)} style={inp}/>
            </Field>
            <Field label="Display Order" hint="Lower = shows first">
              <input type="number" value={form.display_order||0} onChange={e=>update("display_order", e.target.value)} style={inp}/>
            </Field>
          </div>

          <Field label="Image (optional)">
            <AddonImageUploader currentUrl={form.image_url} onUploaded={(url) => update("image_url", url)} />
          </Field>

          <Field label="Active">
            <button onClick={()=>update("is_active", !form.is_active)} style={{padding:"7px 14px",background:form.is_active?"linear-gradient(135deg,"+C.green+",#1e6b3f)":C.light,color:form.is_active?"#fff":C.muted,border:"1px solid "+(form.is_active?C.green:C.border),borderRadius:7,fontSize:12,fontWeight:600,cursor:"pointer",fontFamily:F}}>
              {form.is_active ? "✓ Active - shows on booking page" : "Inactive - hidden"}
            </button>
          </Field>

          {error && (
            <div style={{padding:"10px 14px",background:C.redL,color:C.red,border:"1px solid "+C.red+"40",borderRadius:7,fontSize:12,marginTop:10}}>
              ❌ {error}
            </div>
          )}

          <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16,paddingTop:14,borderTop:"1px solid "+C.border}}>
            <button onClick={onClose} style={{padding:"9px 16px",background:C.light,color:C.muted,border:"1px solid "+C.border,borderRadius:7,fontSize:13,cursor:"pointer",fontFamily:F}}>Cancel</button>
            <button onClick={save} disabled={saving} style={{padding:"9px 20px",background:"linear-gradient(135deg,"+C.gold+","+C.goldD+")",color:"#fff",border:"none",borderRadius:7,fontSize:13,fontWeight:700,cursor:saving?"not-allowed":"pointer",fontFamily:F,opacity:saving?0.6:1}}>
              {saving?"Saving...":(addon?"Save Changes":"Create Add-on")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div style={{marginBottom:14}}>
      <label style={{display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:5,fontFamily:F}}>{label}</label>
      {children}
      {hint && <div style={{fontSize:10,color:C.dim,marginTop:4}}>{hint}</div>}
    </div>
  );
}
